/**
 * CallRequiredTasks — Iter-163 · Phase 5.
 *
 * Finance-only section inside Billing & A/R. Lists overdue accounts
 * needing a phone call, sorted by days_overdue DESC. Finance can
 * mark each task complete inline.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { PhoneCall, CheckCircle2, RefreshCw, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CallRequiredTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/billing-notifications/call-required?status=open`, { credentials: 'include' });
      if (r.ok) { const d = await r.json(); setTasks(d.tasks || []); }
    } catch (_) {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (task_id) => {
    setBusyId(task_id);
    try {
      await fetch(`${API}/api/billing-notifications/call-required/complete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id }),
      });
      await load();
    } catch (_) {}
    setBusyId(null);
  };

  return (
    <Card className="p-4 bg-[#141414] border-zinc-800 space-y-3" data-testid="call-required-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PhoneCall className="h-4 w-4 text-red-400" />
          <p className="text-[10px] uppercase tracking-widest text-zinc-300">
            Call Required
            <span className="ml-2 text-white font-bold" data-testid="call-required-count">
              {tasks.length}
            </span>
          </p>
        </div>
        <Button size="sm" onClick={load}
                className="text-[10px] uppercase"
                style={{ backgroundColor: '#27272a', color: '#fff' }}
                data-testid="call-required-refresh">
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>
      <div className="rounded border border-red-500/30 bg-red-500/5 overflow-x-auto">
        <table className="admin-stack-table text-xs" data-testid="call-required-table">
          <thead className="bg-black/40 text-zinc-400 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left p-2">Days Overdue</th>
              <th className="text-left p-2">Property</th>
              <th className="text-left p-2">PM</th>
              <th className="text-left p-2">Invoice</th>
              <th className="text-right p-2">Amount Due</th>
              <th className="text-left p-2">Contact</th>
              <th className="text-center p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="p-4 text-center text-zinc-500">Loading…</td></tr>}
            {!loading && tasks.length === 0 && (
              <tr><td colSpan="7" className="p-4 text-center text-zinc-500" data-testid="call-required-empty">
                No overdue accounts require calls right now.
              </td></tr>
            )}
            {tasks.map((t) => (
              <tr key={t.task_id} className="border-t border-zinc-800" data-testid={`call-required-row-${t.task_id}`}>
                <td className="p-2" data-label="Days Overdue">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white"
                        data-testid={`call-required-days-${t.task_id}`}>
                    {t.days_overdue || 0}d
                  </span>
                </td>
                <td className="p-2 text-white" data-label="Property">{t.prop_name || t.property_id}</td>
                <td className="p-2 text-zinc-300" data-label="PM">{t.pm_name || '—'}</td>
                <td className="p-2 text-zinc-300" data-label="Invoice">{t.invoice_number || '—'}</td>
                <td className="p-2 text-right text-white" data-label="Amount Due">
                  {typeof t.amount === 'number' ? `$${t.amount.toLocaleString()}` : '—'}
                </td>
                <td className="p-2 text-zinc-400 text-[10px]" data-label="Contact">
                  {t.pm_email && <div>{t.pm_email}</div>}
                  {t.pm_phone && <div>{t.pm_phone}</div>}
                </td>
                <td className="p-2 text-center" data-label="Action">
                  <Button size="sm" onClick={() => complete(t.task_id)} disabled={busyId === t.task_id}
                          className="text-[10px] uppercase font-bold"
                          style={{ backgroundColor: '#10b981', color: '#fff' }}
                          data-testid={`call-required-complete-${t.task_id}`}>
                    {busyId === t.task_id
                      ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      : <CheckCircle2 className="h-3 w-3 mr-1" />}
                    Mark called
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
